/**
 * @author Harish.R <harish.r@314ecorp.com>
 * constants for eligibility response
 */
import _ from 'lodash';
import { ManipulateType } from 'dayjs';
import { DeleteOne, CloseSmall } from '@icon-park/react';
import { EMPTY_VALUE, EMPTY_VALUE_FOR_DATE_RANGE, MONTH_DATE_YEAR_SLASH_FORMAT } from '@veritable/common/constants';

import dateParser from 'lib/dateParser';
import { BENEFIT_TYPES } from './benefit';
import { ANT_TAG_COLORS } from '@veritable/common/constants/antd';
import states from 'app-constants/states.json';

const GRAY_COLOR = '#595959';

const GREEN_HEX_CODE = '#52c41a';

const RED_HEX_CODE = '#E05858';

const GREEN_ACTIVE_HEX_CODE = '#22a077';

const INPUT_ERROR_HEX_CODE = '#F5875B';

const IMPLEMENTATION_ERROR_HEX_CODE = '#F0A54A';

const BLUE_HEX_CODE = '#00ABC7';

const EMPTY_MESSAGE = 'No Record Found';

const LOADING_MESSAGE = 'Loading search results...';

const ELIGIBILITY_PATIENT_SEARCH_TYPE: EligibilityRequests.IPatientSearchType = 'eligibility';

const ELIGIBILITY_AND_CLAIMS_MODAL_CONTENT = 'This payer is available for both Eligibility and Claim Status Checks.';

const ELIGIBILITY_MODAL_CONTENT = 'This payer is available only for Eligibility Verification.';

const ELIGIBILITY_UNAVAILABLE_PAYER_LABEL = 'Payers not available for Eligibility';

const SUCCESS_REQUEST_CODE = 'Success';

const SKIPPED_REQUESTS_CODE = 'skipped';

const UNPROCESSED_REQUESTS_CODE = 'unprocessed';

const SCHEDULED_REQUEST_CODE = 'scheduled';

const INPROGRESS_REQUESTS_CODE = 'inProgress';

const ELIGIBILITY_COVERAGE_LEVELS = [
	{
		code_value: 'CHD',
		content: 'Children Only',
		color: ANT_TAG_COLORS.volcano,
	},
	{
		code_value: 'DEP',
		content: 'Dependents Only',
		color: ANT_TAG_COLORS.geekblue,
	},
	{
		code_value: 'E1D',
		content: 'Employee and One Dependent',
		color: ANT_TAG_COLORS.geekblue,
	},
	{
		code_value: 'E2D',
		content: 'Employee and Two Dependents',
		color: ANT_TAG_COLORS.cyan,
	},
	{
		code_value: 'E3D',
		content: 'Employee and Three Dependents',
		color: ANT_TAG_COLORS.cyan,
	},
	{
		code_value: 'E5D',
		content: 'Employee and One or More Dependents',
		color: ANT_TAG_COLORS.cyan,
	},
	{
		code_value: 'E6D',
		content: 'Employee and Two or More Dependents',
		color: ANT_TAG_COLORS.cyan,
	},
	{
		code_value: 'E7D',
		content: 'Employee and Three or More Dependents',
		color: ANT_TAG_COLORS.cyan,
	},
	{
		code_value: 'E8D',
		content: 'Employee and Four or More Dependents',
		color: ANT_TAG_COLORS.cyan,
	},
	{
		code_value: 'E9D',
		content: 'Employee and Five or More Dependents',
		color: ANT_TAG_COLORS.cyan,
	},
	{
		code_value: 'ECH',
		content: 'Employee and Children',
		color: ANT_TAG_COLORS.cyan,
	},
	{
		code_value: 'EMP',
		content: 'Employee Only',
		color: ANT_TAG_COLORS.cyan,
	},
	{
		code_value: 'ESP',
		content: 'Employee and Spouse',
		color: ANT_TAG_COLORS.cyan,
	},
	{
		code_value: 'FAM',
		content: 'Family',
		color: ANT_TAG_COLORS.purple,
	},
	{
		code_value: 'IND',
		content: 'Individual',
		color: ANT_TAG_COLORS.magenta,
	},
	{
		code_value: 'S1C',
		content: 'Subscriber and 1 child',
		color: ANT_TAG_COLORS.cyan,
	},
	{
		code_value: 'S5C',
		content: 'Subscriber and 1 or more children',
		color: ANT_TAG_COLORS.cyan,
	},
	{
		code_value: 'S6C',
		content: 'Subscriber and 2 or more children',
		color: ANT_TAG_COLORS.cyan,
	},
	{
		code_value: 'SPC',
		content: 'Spouse and Children',
		color: ANT_TAG_COLORS.magenta,
	},
	{
		code_value: 'SPO',
		content: 'Spouse Only',
		color: ANT_TAG_COLORS.magenta,
	},
	{
		code_value: 'SS1',
		content: 'Subscriber, spouse, and 1 child',
		color: ANT_TAG_COLORS.cyan,
	},
	{
		code_value: 'SS5',
		content: 'Subscriber, spouse, and 1 or more children',
		color: ANT_TAG_COLORS.cyan,
	},
	{
		code_value: 'SS6',
		content: 'Subscriber, spouse, and 2 or more children',
		color: ANT_TAG_COLORS.cyan,
	},
	{
		code_value: 'SSP',
		content: 'Subscriber and spouse',
		color: ANT_TAG_COLORS.cyan,
	},
	{
		code_value: 'TWO',
		content: 'Two Party',
		color: ANT_TAG_COLORS.cyan,
	},
];

const ELIGIBILITY_PAYER_TAG_STATUS = {
	active: {
		label: 'Active',
		color: '#52C41A',
		tagColor: ANT_TAG_COLORS.green,
	},
	inactive: {
		label: 'Inactive',
		color: '#FF4D4F',
		tagColor: ANT_TAG_COLORS.red,
	},
	pending: {
		label: 'Pending',
		color: '#FFA940',
		tagColor: ANT_TAG_COLORS.orange,
	},
	archived: {
		label: 'Archived',
		color: '#595959',
		tagColor: ANT_TAG_COLORS.default,
	},
	unknown: {
		label: 'Unknown',
		color: '#949494',
		tagColor: ANT_TAG_COLORS.default,
	},
};

const PARENT_CATEGORIES = {
	active: 'Active',
	inactive: 'Inactive',
	implementationError: 'Implementation Error',
	unknown: 'Unknown',
};

const ELIGIBILITY_RESPONSE_TAG_STATUS = {
	1: {
		label: 'Active Coverage',
		color: GREEN_ACTIVE_HEX_CODE,
		tagColor: ANT_TAG_COLORS.success,
		parentCategory: PARENT_CATEGORIES.active,
	},
	2: {
		label: 'Active - Full Risk Capitation',
		color: GREEN_ACTIVE_HEX_CODE,
		tagColor: ANT_TAG_COLORS.success,
		parentCategory: PARENT_CATEGORIES.active,
	},
	3: {
		label: 'Active - Services Capitated',
		color: GREEN_ACTIVE_HEX_CODE,
		tagColor: ANT_TAG_COLORS.success,
		parentCategory: PARENT_CATEGORIES.active,
	},
	4: {
		label: 'Active - Services Capitated to Primary Care Physician',
		color: GREEN_ACTIVE_HEX_CODE,
		tagColor: ANT_TAG_COLORS.success,
		parentCategory: PARENT_CATEGORIES.active,
	},
	5: {
		label: 'Active - Pending Investigation',
		color: GREEN_ACTIVE_HEX_CODE,
		tagColor: ANT_TAG_COLORS.success,
		parentCategory: PARENT_CATEGORIES.active,
	},
	6: {
		label: 'Inactive',
		color: RED_HEX_CODE,
		tagColor: ANT_TAG_COLORS.red,
		parentCategory: PARENT_CATEGORIES.inactive,
	},

	7: {
		label: 'Inactive - Pending Eligibility Update',
		color: RED_HEX_CODE,
		tagColor: ANT_TAG_COLORS.red,
		parentCategory: PARENT_CATEGORIES.inactive,
	},
	8: {
		label: 'Inactive - Pending Investigation',
		color: RED_HEX_CODE,
		tagColor: ANT_TAG_COLORS.red,
		parentCategory: PARENT_CATEGORIES.inactive,
	},
	NoResponseError: {
		label: 'No Response Error',
		color: '#7A7777',
		tagColor: ANT_TAG_COLORS.error,
		parentCategory: 'No Response Error',
	},
	InputError: {
		label: 'Input Error',
		color: INPUT_ERROR_HEX_CODE,
		tagColor: ANT_TAG_COLORS.error,
		parentCategory: 'Input Error',
	},
	ValidationError: {
		label: 'Implementation Error',
		color: IMPLEMENTATION_ERROR_HEX_CODE,
		tagColor: ANT_TAG_COLORS.error,
		parentCategory: PARENT_CATEGORIES.implementationError,
	},
	NetworkError: {
		label: 'Communication Error',
		color: '#F0A54A',
		tagColor: ANT_TAG_COLORS.error,
		parentCategory: 'Communication Error',
	},
	ServerError: {
		label: 'Implementation Error',
		color: IMPLEMENTATION_ERROR_HEX_CODE,
		tagColor: ANT_TAG_COLORS.error,
		parentCategory: PARENT_CATEGORIES.implementationError,
	},
	authError: {
		label: 'Implementation Error',
		color: IMPLEMENTATION_ERROR_HEX_CODE,
		tagColor: ANT_TAG_COLORS.error,
		parentCategory: PARENT_CATEGORIES.implementationError,
	},
	ServiceError: {
		label: 'Implementation Error',
		color: IMPLEMENTATION_ERROR_HEX_CODE,
		tagColor: ANT_TAG_COLORS.error,
		parentCategory: PARENT_CATEGORIES.implementationError,
	},
	ServiceAuthError: {
		label: 'Implementation Error',
		color: IMPLEMENTATION_ERROR_HEX_CODE,
		tagColor: ANT_TAG_COLORS.error,
		parentCategory: PARENT_CATEGORIES.implementationError,
	},
	inNetwork: {
		label: 'In Network',
		color: '#40A9FF',
		tagColor: ANT_TAG_COLORS.lime,
	},
	outOfNetwork: {
		label: 'Out of Network',
		color: '#FA8C16',
		tagColor: ANT_TAG_COLORS.volcano,
	},
	notApplicableNetwork: {
		label: 'Network Not Applicable',
		color: '#949494',
		tagColor: ANT_TAG_COLORS.default,
	},
	noNetwork: {
		label: 'No Network',
		color: '#949494',
		tagColor: ANT_TAG_COLORS.default,
	},
	[SKIPPED_REQUESTS_CODE]: {
		label: 'Skipped',
		color: '#7A7777',
		tagColor: ANT_TAG_COLORS.default,
		parentCategory: 'Skipped',
	},
	[UNPROCESSED_REQUESTS_CODE]: {
		label: 'Unprocessed',
		color: '#7A7777',
		tagColor: ANT_TAG_COLORS.default,
		parentCategory: 'Unprocessed',
	},
	[SCHEDULED_REQUEST_CODE]: {
		label: 'Scheduled',
		color: '#7A7777',
		tagColor: ANT_TAG_COLORS.default,
		parentCategory: 'Scheduled',
	},
	[INPROGRESS_REQUESTS_CODE]: {
		label: 'In Progress',
		color: ANT_TAG_COLORS.gold,
		parentCategory: 'In Progress',
	},
	unknown: {
		label: 'Unknown',
		color: '#7A7777',
		tagColor: ANT_TAG_COLORS.default,
		parentCategory: PARENT_CATEGORIES.unknown,
	},
};

const ELIGIBILITY_RESUBMIT_INVALID_STATUS: EligibilityResponse.IEZAPIResponseStatus[] = ['InputError', 'ValidationError'];

const ELIGIBILITY_AUTH_INDICATOR_TAG_STATUS = {
	required: {
		label: 'Auth Required',
		color: '#FF4D4F',
		tagColor: ANT_TAG_COLORS.red,
	},
	notRequired: {
		label: 'Auth Not Required',
		color: '#949494',
		tagColor: ANT_TAG_COLORS.default,
	},
	unknown: {
		label: 'Auth Reqd. Unknown',
		color: '#949494',
		tagColor: ANT_TAG_COLORS.default,
	},
};

const ELIGIBILITY_PAYER_ENROLLMENT_TAG_STATUS = {
	failed: {
		label: 'Rejected',
		description: 'Enrollment Rejected',
		action: 'Mark as Rejected',
		color: '#f5222d',
		tagColor: ANT_TAG_COLORS.volcano,
	},
	completed: {
		label: 'Enrolled',
		description: 'Enrollment Completed',
		action: 'Mark as Enrolled',
		color: GREEN_HEX_CODE,
		tagColor: ANT_TAG_COLORS.success,
	},
	requested: {
		label: 'Requested',
		description: 'Enrollment Requested',
		color: '#fa8c16',
		tagColor: ANT_TAG_COLORS.orange,
	},
	pending: {
		label: 'Pending',
		description: 'Enrollment Pending',
		color: '#f2c94c',
		tagColor: ANT_TAG_COLORS.gold,
	},
	notEnrolled: {
		label: 'Not Enrolled',
		description: 'Not Enrolled',
		color: GRAY_COLOR,
		tagColor: ANT_TAG_COLORS.default,
	},
	notApplicable: {
		label: 'Not Applicable',
		description: 'Enrollment not applicable',
		color: '#949494',
		tagColor: ANT_TAG_COLORS.default,
	},
	unknown: {
		label: 'Unknown',
		description: 'Unknown',
		color: '#949494',
		tagColor: ANT_TAG_COLORS.default,
	},
};

const ELIGIBILITY_BATCH_UPLOAD_STATUS_CODES = {
	canceled: -1 as EligibilityBatchUpload.IBatchTaskStatus,
	inProgress: 1 as EligibilityBatchUpload.IBatchTaskStatus,
	draft: 0 as EligibilityBatchUpload.IBatchTaskStatus,
	completed: 2 as EligibilityBatchUpload.IBatchTaskStatus,
	scheduled: 3 as EligibilityBatchUpload.IBatchTaskStatus,
	partiallyCompleted: 4 as EligibilityBatchUpload.IBatchTaskStatus,
};

const ELIGIBILITY_BATCH_REQUEST_TYPE_STATUS_CODES = {
	batchUpload: 1 as EligibilityBatchUpload.IBatchRequestType,
	resubmission: 2 as EligibilityBatchUpload.IBatchRequestType,
};

const ELIGIBILITY_BATCH_REQUEST_TYPE_STATUS = {
	[ELIGIBILITY_BATCH_REQUEST_TYPE_STATUS_CODES.batchUpload]: {
		label: 'Batch Upload',
	},
	[ELIGIBILITY_BATCH_REQUEST_TYPE_STATUS_CODES.resubmission]: {
		label: 'Resubmission',
	},
};

const ELIGIBILITY_BATCH_UPLOAD_SUCCESSFUL_TRANSACTION_STATUSES = _.keys(BENEFIT_TYPES);

const ELIGIBILITY_FAILED_TRANSACTION_STATUSES: EligibilityRequests.EZAPIResponseStatus[] = [
	'InputError',
	'NoResponseError',
	'ValidationError',
	'ServerError',
	'NetworkError',
	'AuthError',
	'ServiceError',
	'ServiceAuthError',
];

const ELIGIBILITY_BATCH_UPLOAD_STATUS = {
	[ELIGIBILITY_BATCH_UPLOAD_STATUS_CODES.inProgress]: {
		label: 'In Progress',
		color: '#1d39c4',
		tagColor: ANT_TAG_COLORS.geekblue,
	},
	[ELIGIBILITY_BATCH_UPLOAD_STATUS_CODES.completed]: {
		label: 'Completed',
		color: '#52c41a',
		tagColor: ANT_TAG_COLORS.success,
	},
	[ELIGIBILITY_BATCH_UPLOAD_STATUS_CODES.partiallyCompleted]: {
		label: 'Partially Completed',
		color: '#08979c',
		tooltip: 'This batch could only be partially completed as one or more payers reached the failure limit.',
		tagColor: ANT_TAG_COLORS.cyan,
	},
	// [ELIGIBILITY_BATCH_UPLOAD_STATUS_CODES.scheduled]: {
	// 	label: 'Scheduled',
	// 	color: '#d46b08',
	// 	tagColor: ANT_TAG_COLORS.orange,
	// },
	[ELIGIBILITY_BATCH_UPLOAD_STATUS_CODES.canceled]: {
		label: 'Canceled',
		color: '#ff4d4f',
		tagColor: ANT_TAG_COLORS.error,
	},
	[ELIGIBILITY_BATCH_UPLOAD_STATUS_CODES.draft]: {
		label: 'Draft',
		color: '#262626',
		tagColor: ANT_TAG_COLORS.default,
	},
};

const ELIGIBILITY_BATCH_REQUEST_STATUS_OPTIONS: IMultiSelectOption[] = _.map(ELIGIBILITY_BATCH_UPLOAD_STATUS, ({ label, color }, key) => ({
	label,
	value: _.toNumber(key) as EligibilityBatchUpload.IBatchTaskStatus,
	color,
}));

const ELIGIBILITY_BATCH_ACTIONS_MODAL_CONFIG = {
	active: {
		okText: 'Cancel Batch',
		cancelText: "Don't Cancel",
		modalDescriptionText: 'Please add some comment before cancelling the batch request.',
		tooltipTitle: 'Cancel Batch',
		Icon: CloseSmall,
	},
	draft: {
		okText: 'Delete',
		cancelText: "Don't Delete",
		modalDescriptionText: 'Please add some comment before deleting the batch request.',
		tooltipTitle: 'Delete Batch',
		Icon: DeleteOne,
	},
};

const ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES = {
	failed: -1 as Payers.IEnrollmentStatus,
	completed: 0 as Payers.IEnrollmentStatus,
	pending: 1 as Payers.IEnrollmentStatus,
	requested: 2 as Payers.IEnrollmentStatus,
	notEnrolled: 3 as Payers.IEnrollmentStatus,
	notApplicable: 4 as Payers.IEnrollmentStatus,
};

const ENROLLMENT_STATUS_OPTIONS = [
	{
		label: ELIGIBILITY_PAYER_ENROLLMENT_TAG_STATUS.completed.label,
		value: ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.completed,
		color: ELIGIBILITY_PAYER_ENROLLMENT_TAG_STATUS.completed.color,
	},
	{
		label: ELIGIBILITY_PAYER_ENROLLMENT_TAG_STATUS.requested.label,
		value: ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.requested,
		color: ELIGIBILITY_PAYER_ENROLLMENT_TAG_STATUS.requested.color,
	},
	{
		label: ELIGIBILITY_PAYER_ENROLLMENT_TAG_STATUS.notEnrolled.label,
		value: ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.notEnrolled,
		color: ELIGIBILITY_PAYER_ENROLLMENT_TAG_STATUS.notEnrolled.color,
	},
	{
		label: ELIGIBILITY_PAYER_ENROLLMENT_TAG_STATUS.failed.label,
		value: ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.failed,
		color: ELIGIBILITY_PAYER_ENROLLMENT_TAG_STATUS.failed.color,
	},
];

const ELIGIBILITY_PAYER_STATUS_CODES = {
	active: 0 as Payers.IPayerStatus,
	pending: 1 as Payers.IPayerStatus,
	inactive: 2 as Payers.IPayerStatus,
	archived: 3 as Payers.IPayerStatus,
};

const ELIGIBILITY_NETWORK_TYPES = {
	inNetwork: 'In Network',
	outOfNetwork: 'Out Of Network',
	notApplicableNetwork: 'Network Not Applicable',
	noNetwork: 'No Network',
};

const ELIGIBILITY_AUTH_INDICATOR_CODES = {
	required: 'Y' as EligibilityResponse.IAuthIndicator,
	notRequired: 'N' as EligibilityResponse.IAuthIndicator,
	unknown: 'U' as EligibilityResponse.IAuthIndicator,
};

const ELIGIBILITY_PAYER_STATUS = {
	[ELIGIBILITY_PAYER_STATUS_CODES.active]: ELIGIBILITY_PAYER_TAG_STATUS.active,
	[ELIGIBILITY_PAYER_STATUS_CODES.inactive]: ELIGIBILITY_PAYER_TAG_STATUS.inactive,
	[ELIGIBILITY_PAYER_STATUS_CODES.pending]: ELIGIBILITY_PAYER_TAG_STATUS.pending,
	[ELIGIBILITY_PAYER_STATUS_CODES.archived]: ELIGIBILITY_PAYER_TAG_STATUS.archived,
};

const ELIGIBILITY_NETWORK_STATUS = {
	[ELIGIBILITY_NETWORK_TYPES.inNetwork]: ELIGIBILITY_RESPONSE_TAG_STATUS.inNetwork,
	[ELIGIBILITY_NETWORK_TYPES.outOfNetwork]: ELIGIBILITY_RESPONSE_TAG_STATUS.outOfNetwork,
	[ELIGIBILITY_NETWORK_TYPES.notApplicableNetwork]: ELIGIBILITY_RESPONSE_TAG_STATUS.notApplicableNetwork,
	[ELIGIBILITY_NETWORK_TYPES.noNetwork]: ELIGIBILITY_RESPONSE_TAG_STATUS.noNetwork,
};

const ELIGIBILITY_AUTH_INDICATOR = {
	[ELIGIBILITY_AUTH_INDICATOR_CODES.required]: ELIGIBILITY_AUTH_INDICATOR_TAG_STATUS.required,
	[ELIGIBILITY_AUTH_INDICATOR_CODES.notRequired]: ELIGIBILITY_AUTH_INDICATOR_TAG_STATUS.notRequired,
	[ELIGIBILITY_AUTH_INDICATOR_CODES.unknown]: ELIGIBILITY_AUTH_INDICATOR_TAG_STATUS.unknown,
};

const ELIGIBILITY_AUTH_INDICATOR_MESSAGES = {
	[ELIGIBILITY_AUTH_INDICATOR_CODES.required]: 'Authorization / Certification is required',
	[ELIGIBILITY_AUTH_INDICATOR_CODES.notRequired]: 'Authorization / Certification is not required',
	[ELIGIBILITY_AUTH_INDICATOR_CODES.unknown]: 'Authorization / Certification requirement is unknown',
};

const ELIGIBILITY_PAYER_ENROLLMENT_STATUS = {
	[ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.failed]: ELIGIBILITY_PAYER_ENROLLMENT_TAG_STATUS.failed,
	[ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.completed]: ELIGIBILITY_PAYER_ENROLLMENT_TAG_STATUS.completed,
	[ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.pending]: ELIGIBILITY_PAYER_ENROLLMENT_TAG_STATUS.pending,
	[ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.requested]: ELIGIBILITY_PAYER_ENROLLMENT_TAG_STATUS.requested,
	[ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.notEnrolled]: ELIGIBILITY_PAYER_ENROLLMENT_TAG_STATUS.notEnrolled,
	[ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.notApplicable]: ELIGIBILITY_PAYER_ENROLLMENT_TAG_STATUS.notApplicable,
};

const ELIGIBILITY_BATCH_UPLOAD_EXPORT_TYPES = {
	SUCCESSFUL: 'successful' as EligibilityBatchUpload.IExportType,
	UNSUCCESSFUL: 'unsuccessful' as EligibilityBatchUpload.IExportType,
	SKIPPED: 'skipped' as EligibilityBatchUpload.IExportType,
};

const ELIGIBILITY_BENIFIT_TYPES = {
	coInsurance: {
		label: 'Co-Insurance',
		key: 'coInsurance',
	},
	coPayment: {
		label: 'Co-Payment',
		key: 'coPayment',
	},
	deductibles: {
		label: 'Deductible',
		key: 'deductibles',
	},
	outOfPocket: {
		label: 'Out of Pocket (Stop Loss)',
		key: 'outOfPocket',
	},
};

const ELIGIBILITY_STATUS_MULTI_SELECT_OPTIONS: IMultiSelectOption[] = [
	{
		value: ELIGIBILITY_RESPONSE_TAG_STATUS[1].parentCategory,
		label: ELIGIBILITY_RESPONSE_TAG_STATUS[1].parentCategory,
		color: ELIGIBILITY_RESPONSE_TAG_STATUS[1].color,
	},
	{
		value: ELIGIBILITY_RESPONSE_TAG_STATUS[6].parentCategory,
		label: ELIGIBILITY_RESPONSE_TAG_STATUS[6].parentCategory,
		color: ELIGIBILITY_RESPONSE_TAG_STATUS[6].color,
	},
	{
		value: ELIGIBILITY_RESPONSE_TAG_STATUS.InputError.parentCategory,
		label: ELIGIBILITY_RESPONSE_TAG_STATUS.InputError.parentCategory,
		color: ELIGIBILITY_RESPONSE_TAG_STATUS.InputError.color,
	},
	{
		value: ELIGIBILITY_RESPONSE_TAG_STATUS.NetworkError.parentCategory,
		label: ELIGIBILITY_RESPONSE_TAG_STATUS.NetworkError.parentCategory,
		color: ELIGIBILITY_RESPONSE_TAG_STATUS.NetworkError.color,
	},
	{
		value: ELIGIBILITY_RESPONSE_TAG_STATUS.ServiceError.parentCategory,
		label: ELIGIBILITY_RESPONSE_TAG_STATUS.ServiceError.parentCategory,
		color: ELIGIBILITY_RESPONSE_TAG_STATUS.ServiceError.color,
	},
	{
		value: ELIGIBILITY_RESPONSE_TAG_STATUS.NoResponseError.parentCategory,
		label: ELIGIBILITY_RESPONSE_TAG_STATUS.NoResponseError.parentCategory,
		color: ELIGIBILITY_RESPONSE_TAG_STATUS.NoResponseError.color,
	},
	{
		value: ELIGIBILITY_RESPONSE_TAG_STATUS.unknown.parentCategory,
		label: ELIGIBILITY_RESPONSE_TAG_STATUS.unknown.parentCategory,
		color: ELIGIBILITY_RESPONSE_TAG_STATUS.unknown.color,
	},
];

/**
 * gender types of a patient for eligibility check
 */
const ELIGIBILITY_PATIENT_GENDERS: EligibilityRequestForm.IPatientGender[] = ['male', 'female', 'other', 'unknown'];

/**
 * default relation type of the subscriber with the patient for eligibility check
 */
const ELIGIBILITY_PATIENT_DEFAULT_RELATIONSHIP: EligibilityRequestForm.IPatientRelationship = 'self';

/**
 * relation types of the subscriber with the patient for eligibility check
 */
const ELIGIBILITY_PATIENT_RELATIONS: EligibilityRequestForm.IPatientRelationship[] = [
	ELIGIBILITY_PATIENT_DEFAULT_RELATIONSHIP,
	'child',
	'spouse',
	'parent',
	'other',
];

const PROVIDER_IDENTIFIERS_FORM_FIELDS: Record<
	keyof EligibilityRequestForm.IProviderIdentifierKeys,
	{ title: string; placeholder: string; errorMessage: string; validationMessage?: string; info?: string }
> = {
	stateLicenseNumber: {
		title: 'State License Number',
		placeholder: 'E.g.: SLN123456',
		errorMessage: 'Please enter a valid State License Number',
	},
	facilityIDNumber: {
		title: 'Facility ID Number',
		placeholder: 'E.g.: 123456',
		errorMessage: 'Please enter a valid Facility ID Number',
	},
	personalIdentificationNumber: {
		title: 'Personal Identification Number (PIN)',
		placeholder: 'E.g.: 987654321',
		errorMessage: 'Please enter a valid Personal Identification Number',
	},
	contractNumber: {
		title: 'Contract Number',
		placeholder: 'E.g.: CON-56789',
		errorMessage: 'Please enter a valid Contract Number',
	},
	electronicDevicePINNumber: {
		title: 'Electronic Device PIN Number',
		placeholder: 'E.g.: 6789012',
		errorMessage: 'Please enter a valid Electronic Device PIN Number',
	},
	submitterIdentificationNumber: {
		title: 'Submitter Identification Number',
		placeholder: 'E.g.: SUB12345678',
		errorMessage: 'Please enter a valid Submitter Identification Number',
	},
	userIdentification: {
		title: 'User Identification',
		placeholder: 'E.g.: USER1234',
		errorMessage: 'Please enter a valid User Identification',
	},
	providerPlanNetworkIDNumber: {
		title: 'Provider Plan Network ID Number',
		placeholder: 'E.g.: 12345678',
		errorMessage: 'Please enter a valid Provider Plan Network ID Number',
	},
	priorIdentifierNumber: {
		title: 'Prior Identifier Number',
		placeholder: 'E.g.: 1234567',
		errorMessage: 'Please enter a valid Prior Identifier Number',
	},
	facilityNetworkIdentificationNumber: {
		title: 'Facility Network Identification Number',
		placeholder: 'E.g.: 123456789',
		errorMessage: 'Please enter a valid Facility Network Identification Number',
	},
};

const ELIGIBILITY_FORM_FIELDS: Record<
	keyof EligibilityRequestForm.IFormModel,
	{ title: string; placeholder: string; errorMessage: string; validationMessage?: string; info?: string }
> = {
	npi: {
		title: 'Provider/Organization',
		placeholder: 'Select a Provider/Organization',
		errorMessage: 'Please select a Provider/Organization',
		info: 'Choose a Provider/Organization from the dropdown to make the request.',
	},
	locationId: {
		title: 'Service Location',
		placeholder: 'Select a Service Location',
		errorMessage: 'Please select a service location',
		info: 'This is the location as specified in the input at the time of requesting.',
	},
	payerId: {
		title: 'Payer',
		placeholder: 'Select a Payer',
		errorMessage: 'Please select a Payer',
		info: 'Please select the payer to which you are submitting the transaction.',
	},
	relation: {
		title: 'Relationship to the Subscriber',
		placeholder: 'Self',
		errorMessage: 'Please select a Relationship',
	},
	memberId: {
		title: 'Member ID',
		placeholder: 'E.g.: WXI234FJCHI',
		errorMessage: 'Please enter a valid Member ID',
	},
	ssn: {
		title: 'SSN',
		placeholder: 'E.g.: 000000000',
		errorMessage: 'Please enter a valid SSN',
	},
	medicaidId: {
		title: 'Medicaid ID',
		placeholder: 'E.g.: WXI234FJCHI',
		errorMessage: 'Please enter a valid Medicaid ID',
	},
	groupNumber: {
		title: 'Group Number',
		placeholder: 'E.g.: WXI234FJCHI',
		errorMessage: 'Please enter a valid Group Number',
	},

	firstName: {
		title: 'First Name',
		placeholder: 'E.g.: John',
		errorMessage: 'Please enter the First Name',
	},
	lastName: {
		title: 'Last Name',
		placeholder: 'E.g.: Doe',
		errorMessage: 'Please enter the Last Name',
	},
	dateOfBirth: {
		title: 'Date of Birth',
		placeholder: MONTH_DATE_YEAR_SLASH_FORMAT,
		errorMessage: 'Please enter the Date of Birth',
	},
	gender: {
		title: 'Gender',
		placeholder: 'Select a Gender',
		errorMessage: 'Please select a Gender',
	},
	serviceStartDate: {
		title: 'Service Start Date',
		placeholder: dateParser().startOf('day').format(MONTH_DATE_YEAR_SLASH_FORMAT),
		errorMessage: 'Please enter the service start date',
		info: "Enter the date, starting from which you are verifying the patient's eligibility and benefits information. Payers have different requirements for the date. Some payers allow entering a date upto 25 months in the past and up to 12 months in the future. By default, the field is populated with the present day date. ",
	},
	serviceEndDate: {
		title: 'Service End Date',
		placeholder: MONTH_DATE_YEAR_SLASH_FORMAT,
		errorMessage: 'Please enter the service end date',
		info: 'Enter the date on which the service ends. ',
	},
	serviceType: {
		title: 'Service Type',
		placeholder: 'Select the Service Types',
		errorMessage: 'Please select at least one service type.',
		validationMessage: 'You can select only one specific service type at a time.',
		info: 'Select the type of benefit or service for which you want to determine the patient’s eligibility. You can select multiple service types in the field. Some service types may be associated with one or more benefits. For many payers, Health Benefit Plan Coverage is the default service type. Selecting a more specific type will result in more detailed benefit information, if the payer allows to do so.',
	},
	patientReqFields: {
		title: 'Search Patient by',
		placeholder: 'Select a combination',
		errorMessage: 'Please select a combination',
	},
	state: {
		title: 'State',
		placeholder: 'E.g.: Iowa',
		errorMessage: 'Please select a state',
	},
	providerReqFields: {
		title: 'Search Provider by',
		placeholder: 'Select a combination',
		errorMessage: 'Please select a combination',
	},
	...PROVIDER_IDENTIFIERS_FORM_FIELDS,
};

const REQUIRED_FIELD_MESSAGES: Record<keyof EligibilityRequestForm.IFormModel, string> = _.mapValues(
	ELIGIBILITY_FORM_FIELDS,
	(field) => field.errorMessage,
);

const ELIGIBILITY_LIST_VIEW_FIELDS = {
	simpleSearch: {
		placeholder: 'Search by Patient Name',
	},
	eligibilityId: {
		label: 'Eligibility ID',
		placeholder: 'E.g.: 123456789',
	},
	patientName: {
		label: 'Patient Name',
		placeholder: 'E.g.: John Doe',
	},
	identifier: {
		label: 'Patient ID',
		placeholder: 'Enter Member ID, MRN, SSN, etc.',
		tooltip: 'This is an identifier unique to the patient such as Member ID, SSN, Group Number or Medicaid ID.',
	},
	payer: {
		label: 'Payer',
		placeholder: 'Select a Payer',
		selectAllLabel: 'All Payers',
	},
	inquiryDate: {
		label: 'Inquiry Date',
		placeholder: MONTH_DATE_YEAR_SLASH_FORMAT,
	},
	dateOfService: {
		label: 'Service Date',
		placeholder: MONTH_DATE_YEAR_SLASH_FORMAT,
	},

	// filter section
	provider: {
		label: 'Provider/Org',
		placeholder: 'Select the Provider/Organization',
	},
	eligibilityStatus: {
		label: 'Status',
		placeholder: 'Select an Eligibility Status',
		tooltip: `Choose the eligibility status by which you wish you to view the previous responses:</br>
     "Active" indicates the patient is eligible to receive coverage by the payer as of the inquiry date.</br>
     "Inactive" indicates the patient is ineligible to receive coverage by the payer as of the inquiry date.</br>
     "Input Error" indicates there was a problem with the eligibility request made. Review and resubmit.</br>
     "Network Error" indicates there was a problem at our/payer's end and the request had failed, retry.`,
	},
	insuranceType: {
		label: 'Insurance Type',
		placeholder: 'Select the Insurance Type',
	},
	additionalPayer: {
		label: 'Additional Insurance',
		placeholder: 'Select Additional Insurance',
	},
	searchLocation: {
		label: 'Service Location',
		placeholder: 'Select the Location',
	},
	filterButtons: { reset: 'Clear', submit: 'Search', apply: 'Apply' },
};

// TODO: rework on placeholder
const ELIGIBILITY_BATCH_LIST_VIEW_FIELDS = {
	batchID: {
		label: 'Batch ID',
		placeholder: 'E.g.: 100000',
	},
	status: {
		label: 'Status',
		placeholder: 'Select a Status',
	},
	createdBy: {
		label: 'Created By',
		placeholder: 'Select a User',
	},
	creationDate: {
		label: 'Created On',
		placeholder: 'Select a Date',
	},
	filterButtons: { reset: 'Clear', submit: 'Apply' },
};

const PAYER_STATUS_TOOLTIP_TEXT = {
	[ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.notEnrolled]:
		'To activate this payer for you, please initiate enrollment for this payer first. ',
	[ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.requested]: 'To activate this payer for you, please wait for enrollment to be completed. ',
	[ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.pending]: 'To activate this payer for you, please wait for enrollment to be completed. ',
	[ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.failed]: 'You cannot activate this payer as your enrollment has been rejected. ',
	[ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.completed]: '',
};

/**
 * create label and value for gender and relation type
 */
const ELIGIBILITY_GENDER_OPTIONS = _.map(ELIGIBILITY_PATIENT_GENDERS, (genderType) => ({
	label: _.upperFirst(genderType),
	value: genderType,
}));

const ELIGIBILITY_PATIENT_RELATIONSHIP_OPTIONS = _.map(ELIGIBILITY_PATIENT_RELATIONS, (relationType) => ({
	label: _.upperFirst(relationType),
	value: relationType,
}));

const ELIGIBILITY_COVERAGE_LEVEL_COLORS = _.mapValues(
	_.keyBy(ELIGIBILITY_COVERAGE_LEVELS, ({ content }) => content),
	({ color }) => color,
);

const PAYER_PROVIDER_ERRORS = {
	provider: {
		title: "Couldn't find provider & payer",
		content:
			'We are unable to proceed with your request. Please check for the following:\n\n' +
			'1. Please check if the provider NPI has been added and if electronic identity verification for the NPI has been completed.\n' +
			'2. Please check if the payer has been added and enabled for the provider. You can proceed to the "Payers" section and check for the same.\n' +
			'3. Please check if your requested location belongs to your preferred locations.',
	},

	payer: {
		title: "Couldn't find payer",
		content:
			'We are unable to proceed with your request. Please check if the payer has been added. You can proceed to the "Payers" section and check for the same.',
	},
};

const EMPTY_VALUES_LIST = [
	EMPTY_VALUE,
	EMPTY_VALUE_FOR_DATE_RANGE,
	`${EMPTY_VALUE_FOR_DATE_RANGE} - ${EMPTY_VALUE_FOR_DATE_RANGE}`,
	null,
	undefined,
	'',
	' ',
];

const DEFAULT_MIN_SERVICE_TYPE_COUNT = 1;

const DEFAULT_MAX_SERVICE_TYPE_COUNT = 2;

const BATCH_ENTITY_MAP_STATUS = {
	skipped: 0 as EligibilityBatchUpload.IEntityMapStatus,
	mapped: 1 as EligibilityBatchUpload.IEntityMapStatus,
};

const DEFAULT_ELIGIBILITY_SERVICE_TYPE_CODE = '30';

const TIME_UNITS: ManipulateType[] = ['years', 'months', 'weeks', 'days'];

const ELIGIBILITY_NETWORK_FILTER_MAPPING = {
	allNetworks: {
		label: 'All Networks',
		filter: '',
	},
	outOfNetwork: {
		label: 'Out Of Network',
		filter: 'In Network',
	},
	inNetwork: {
		label: 'In Network',
		filter: 'Out Of Network',
	},
};

const HEADER_MAPPING_FIELDS_PLACEHOLDER = 'Select Column Name';

const STATE_CODE_OPTIONS = _.map(states, ({ label, value }) => ({
	label: `${label} (${value})`,
	value,
}));

const BATCH_UPLOAD_HEADER_MAPPING_FIELDS_CONFIG: Record<
	EligibilityBatchUpload.IHeaderMappingSchemaKeys,
	{
		title: string;
		placeholder: string;
		tooltip: React.ReactNode;
		errorMessage?: string;
	}
> = {
	payer: {
		title: 'Payer',
		placeholder: HEADER_MAPPING_FIELDS_PLACEHOLDER,
		tooltip: 'Insurance payer name for this request.',
		errorMessage: 'Payer mapping is required',
	},
	provider: {
		title: 'Provider',
		placeholder: HEADER_MAPPING_FIELDS_PLACEHOLDER,
		tooltip: "Provider's name or NPI.",
		errorMessage: 'Provider mapping is required',
	},
	memberId: {
		title: 'Member ID',
		placeholder: HEADER_MAPPING_FIELDS_PLACEHOLDER,
		tooltip: 'Payer-specific insurance member ID or equivalent patient identifier.',
		errorMessage: 'Member ID mapping is required',
	},
	patientFirstName: {
		title: 'Patient First Name',
		placeholder: HEADER_MAPPING_FIELDS_PLACEHOLDER,
		tooltip: "Patient's first name as specified on the insurance member ID card.",
		errorMessage: 'First Name mapping is required',
	},
	patientLastName: {
		title: 'Patient Last Name',
		placeholder: HEADER_MAPPING_FIELDS_PLACEHOLDER,
		tooltip: "Patient's last name as specified on the insurance member ID card.",
		errorMessage: 'Last Name mapping is required',
	},
	patientDOB: {
		title: 'Patient DOB',
		placeholder: HEADER_MAPPING_FIELDS_PLACEHOLDER,
		tooltip: "Patient's date-of-birth as specified on the insurance member ID card.",
		errorMessage: 'DOB mapping is required',
	},
	serviceStartDate: {
		title: 'Service Start Date',
		placeholder: HEADER_MAPPING_FIELDS_PLACEHOLDER,
		tooltip: 'Service date for a single visit, or starting date for a service range.',
		errorMessage: 'Service Start Date mapping is required',
	},
	serviceEndDate: {
		title: 'Service End Date',
		placeholder: HEADER_MAPPING_FIELDS_PLACEHOLDER,
		tooltip: 'End date for a service range.',
	},
	location: {
		title: 'Service Location',
		placeholder: HEADER_MAPPING_FIELDS_PLACEHOLDER,
		tooltip: 'The service location name. Helpful in filtering for multi-location orgs.',
	},
	traceNumber: {
		title: 'Trace Number',
		placeholder: HEADER_MAPPING_FIELDS_PLACEHOLDER,
		tooltip:
			'Trace number is an unique number to track every request/transaction. If you wish to automatically track the requests, you can specify the Trace Number or let us generate one for you.',
		errorMessage: 'Trace Number mapping is required',
	},
	traceEntityId: {
		title: 'Trace Entity ID',
		placeholder: HEADER_MAPPING_FIELDS_PLACEHOLDER,
		tooltip:
			'Trace Entity ID is an unique ID associated with the Trace number. If you wish, you can specify the Trace Entity ID or let us generate one for you.',
		errorMessage: 'Trace Entity ID mapping is required',
	},
	// state: {
	// 	title: 'State',
	// 	placeholder: HEADER_MAPPING_FIELDS_PLACEHOLDER,
	// 	tooltip: (
	// 		<>
	// 			<div>{'Use standard 2-letter state abbreviations only (e.g., CA, NY).'}</div>
	// 			<Button
	// 				type='link'
	// 				href='https://www.faa.gov/air_traffic/publications/atpubs/cnt_html/appendix_a.html'
	// 				target='_blank'
	// 				rel='noopener noreferrer'
	// 				style={{ padding: 0, color: '#fff', textDecoration: 'underline' }}
	// 			>
	// 				{'View full list'}
	// 			</Button>
	// 		</>
	// 	),
	// },
	gender: {
		title: 'Gender',
		placeholder: HEADER_MAPPING_FIELDS_PLACEHOLDER,
		tooltip: 'Male, Female, Other, Unknown.',
	},
	relationshipToSubscriber: {
		title: 'Relationship To Subscriber',
		placeholder: HEADER_MAPPING_FIELDS_PLACEHOLDER,
		tooltip: 'Self, Child, Spouse, Parent, Other.',
	},
	ssn: {
		title: 'SSN',
		placeholder: HEADER_MAPPING_FIELDS_PLACEHOLDER,
		tooltip: '9 digits or XXX-XX-XXXX format.',
	},
	medicaidId: {
		title: 'Medicaid ID',
		placeholder: HEADER_MAPPING_FIELDS_PLACEHOLDER,
		tooltip: 'Medicaid identification number.',
	},
	groupNumber: {
		title: 'Group Number',
		placeholder: HEADER_MAPPING_FIELDS_PLACEHOLDER,
		tooltip: 'Insurance group number.',
	},
};

const BATCH_HEADER_MAPPING_REQUIRED_COLUMNS_COUNT = 7;

const OPTIONAL_PATIENT_FIELD_KEYS: EligibilityBatchUpload.IOptionalPatientFieldKey[] = [
	'medicaidId',
	// 'state',
	'gender',
	'relationshipToSubscriber',
	'ssn',
	'groupNumber',
];

const OPTIONAL_PATIENT_FIELDS_CONFIG = _.map(OPTIONAL_PATIENT_FIELD_KEYS, (key) => ({
	key,
	label: BATCH_UPLOAD_HEADER_MAPPING_FIELDS_CONFIG[key].title,
}));

const ELIGIBILITY_NETWORK_FILTER_OPTIONS = _.map(ELIGIBILITY_NETWORK_FILTER_MAPPING, (network) => network.label);

/**
 * X12 EB-04 Insurance Type codes for filter options
 */
const INSURANCE_TYPE_MULTI_SELECT_OPTIONS: { value: EligibilityRequests.IInsuranceType; label: string }[] = [
	{ label: 'Medicare Secondary Working Aged Beneficiary or Spouse with Employer Group Health Plan', value: '12' },
	{
		label: "Medicare Secondary End-Stage Renal Disease Beneficiary in the Mandated Coordination Period with an Employer's Group Health Plan",
		value: '13',
	},
	{ label: 'Medicare Secondary, No-fault Insurance including Auto is Primary', value: '14' },
	{ label: "Medicare Secondary Worker's Compensation", value: '15' },
	{ label: 'Medicare Secondary Public Health Service (PHS) or Other Federal Agency', value: '16' },
	{ label: 'Medicare Secondary Black Lung', value: '41' },
	{ label: "Medicare Secondary Veteran's Administration", value: '42' },
	{ label: 'Medicare Secondary Disabled Beneficiary Under Age 65 with Large Group Health Plan (LGHP)', value: '43' },
	{ label: 'Medicare Secondary, Other Liability Insurance is Primary', value: '47' },
	{ label: 'Auto Insurance Policy', value: 'AP' },
	{ label: 'Commercial', value: 'C1' },
	{ label: 'Consolidated Omnibus Budget Reconciliation Act (COBRA)', value: 'CO' },
	{ label: 'Medicare Conditionally Primary', value: 'CP' },
	{ label: 'Disability', value: 'D' },
	{ label: 'Disability Benefits', value: 'DB' },
	{ label: 'Exclusive Provider Organization', value: 'EP' },
	{ label: 'Family or Friends', value: 'FF' },
	{ label: 'Group Policy', value: 'GP' },
	{ label: 'Health Maintenance Organization (HMO)', value: 'HM' },
	{ label: 'Health Maintenance Organization (HMO) - Medicare Risk', value: 'HN' },
	{ label: 'Special Low Income Medicare Beneficiary', value: 'HS' },
	{ label: 'Indemnity', value: 'IN' },
	{ label: 'Individual Policy', value: 'IP' },
	{ label: 'Long Term Care', value: 'LC' },
	{ label: 'Long Term Policy', value: 'LD' },
	{ label: 'Life Insurance', value: 'LI' },
	{ label: 'Litigation', value: 'LT' },
	{ label: 'Medicare Part A', value: 'MA' },
	{ label: 'Medicare Part B', value: 'MB' },
	{ label: 'Medicaid', value: 'MC' },
	{ label: 'Medigap Part A', value: 'MH' },
	{ label: 'Medigap Part B', value: 'MI' },
	{ label: 'Medicare Primary', value: 'MP' },
	{ label: 'Other', value: 'OT' },
	{ label: 'Property Insurance - Personal', value: 'PE' },
	{ label: 'Personal', value: 'PL' },
	{ label: 'Personal Payment (Cash - No Insurance)', value: 'PP' },
	{ label: 'Preferred Provider Organization (PPO)', value: 'PR' },
	{ label: 'Point of Service (POS)', value: 'PS' },
	{ label: 'Qualified Medicare Beneficiary', value: 'QM' },
	{ label: 'Property Insurance - Real', value: 'RP' },
	{ label: 'Supplemental Policy', value: 'SP' },
	{ label: 'Tax Equity Fiscal Responsibility Act (TEFRA)', value: 'TF' },
	{ label: 'Workers Compensation', value: 'WC' },
	{ label: 'Wrap Up Policy', value: 'WU' },
];

const ADDITIONAL_PAYER_SELECT_OPTIONS: { value: boolean; label: string }[] = [
	{ value: true, label: 'Yes' },
	{ value: false, label: 'No' },
];

const PAYER_TYPE_OPTIONS: { value: string; label: string }[] = [
	{ label: 'Prior Insurance Carrier', value: 'P4' },
	{ label: 'Plan Sponsor', value: 'P5' },
	{ label: 'Payer', value: 'PR' },
	{ label: 'Primary Payer', value: 'PRP' },
	{ label: 'Secondary Payer', value: 'SEP' },
	{ label: 'Tertiary Payer', value: 'TTP' },
	{ label: 'Vendor', value: 'VN' },
	{ label: 'Managed Care Organization', value: 'Y2' },
	{ label: 'Contracted Service Provider', value: '13' },
	{ label: 'Third-Party Administrator', value: '2B' },
];

export {
	GRAY_COLOR,
	GREEN_HEX_CODE,
	RED_HEX_CODE,
	GREEN_ACTIVE_HEX_CODE,
	BLUE_HEX_CODE,
	EMPTY_MESSAGE,
	LOADING_MESSAGE,
	TIME_UNITS,
	ELIGIBILITY_NETWORK_FILTER_MAPPING,
	ELIGIBILITY_NETWORK_FILTER_OPTIONS,
	ELIGIBILITY_PATIENT_SEARCH_TYPE,
	ELIGIBILITY_COVERAGE_LEVELS,
	ELIGIBILITY_PAYER_STATUS,
	ELIGIBILITY_PAYER_TAG_STATUS,
	ELIGIBILITY_RESPONSE_TAG_STATUS,
	ELIGIBILITY_PAYER_STATUS_CODES,
	ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES,
	ELIGIBILITY_NETWORK_TYPES,
	ELIGIBILITY_NETWORK_STATUS,
	ELIGIBILITY_BATCH_UPLOAD_STATUS_CODES,
	ELIGIBILITY_BATCH_REQUEST_STATUS_OPTIONS,
	ELIGIBILITY_BATCH_LIST_VIEW_FIELDS,
	ELIGIBILITY_BATCH_UPLOAD_STATUS,
	ELIGIBILITY_BATCH_UPLOAD_EXPORT_TYPES,
	ELIGIBILITY_BENIFIT_TYPES,
	ELIGIBILITY_COVERAGE_LEVEL_COLORS,
	ELIGIBILITY_PATIENT_GENDERS,
	ELIGIBILITY_PATIENT_DEFAULT_RELATIONSHIP,
	ELIGIBILITY_PATIENT_RELATIONS,
	ELIGIBILITY_FORM_FIELDS,
	ELIGIBILITY_LIST_VIEW_FIELDS,
	ELIGIBILITY_AUTH_INDICATOR_MESSAGES,
	ELIGIBILITY_AUTH_INDICATOR_CODES,
	ELIGIBILITY_AUTH_INDICATOR_TAG_STATUS,
	ELIGIBILITY_AUTH_INDICATOR,
	ELIGIBILITY_PAYER_ENROLLMENT_STATUS,
	ELIGIBILITY_PAYER_ENROLLMENT_TAG_STATUS,
	ELIGIBILITY_AND_CLAIMS_MODAL_CONTENT,
	ELIGIBILITY_MODAL_CONTENT,
	ELIGIBILITY_UNAVAILABLE_PAYER_LABEL,
	PAYER_STATUS_TOOLTIP_TEXT,
	PAYER_PROVIDER_ERRORS,
	ELIGIBILITY_BATCH_REQUEST_TYPE_STATUS,
	PARENT_CATEGORIES,
	SKIPPED_REQUESTS_CODE,
	UNPROCESSED_REQUESTS_CODE,
	INPROGRESS_REQUESTS_CODE,
	SCHEDULED_REQUEST_CODE,
	BATCH_ENTITY_MAP_STATUS,
	DEFAULT_ELIGIBILITY_SERVICE_TYPE_CODE,
	ELIGIBILITY_RESUBMIT_INVALID_STATUS,
	ELIGIBILITY_BATCH_ACTIONS_MODAL_CONFIG,
	BATCH_UPLOAD_HEADER_MAPPING_FIELDS_CONFIG,
	BATCH_HEADER_MAPPING_REQUIRED_COLUMNS_COUNT,
	OPTIONAL_PATIENT_FIELD_KEYS,
	OPTIONAL_PATIENT_FIELDS_CONFIG,
	PROVIDER_IDENTIFIERS_FORM_FIELDS,
	// computed exports
	REQUIRED_FIELD_MESSAGES,
	ELIGIBILITY_GENDER_OPTIONS,
	ELIGIBILITY_PATIENT_RELATIONSHIP_OPTIONS,
	ELIGIBILITY_STATUS_MULTI_SELECT_OPTIONS,
	INSURANCE_TYPE_MULTI_SELECT_OPTIONS,
	ADDITIONAL_PAYER_SELECT_OPTIONS,
	PAYER_TYPE_OPTIONS,
	EMPTY_VALUES_LIST,
	SUCCESS_REQUEST_CODE,
	ELIGIBILITY_FAILED_TRANSACTION_STATUSES,
	ELIGIBILITY_BATCH_UPLOAD_SUCCESSFUL_TRANSACTION_STATUSES,
	DEFAULT_MAX_SERVICE_TYPE_COUNT,
	DEFAULT_MIN_SERVICE_TYPE_COUNT,
	ENROLLMENT_STATUS_OPTIONS,
	STATE_CODE_OPTIONS,
};
